/**
 * Rol filtre kuralları uçları. Hepsi DAL'da hazırdı (SystemController) — masaüstü
 * ekranı da bunları kullanıyor, yeni uç eklenmedi.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { FilterPolicy, RoleFilterPolicy, SystemRoleSummary } from '../model/types';
import type { SystemObject } from '@/shared/types/system';

const keys = {
  roles: ['policy', 'roles'] as const,
  policies: ['policy', 'filterPolicies'] as const,
  rolePolicies: ['policy', 'roleFilterPolicies'] as const,
  objects: ['policy', 'objects'] as const,
};

export function useRoles() {
  return useQuery({
    queryKey: keys.roles,
    queryFn: () => apiClient.get<SystemRoleSummary[]>(ENDPOINTS.ROLES),
    staleTime: 5 * 60_000,
  });
}

export function useFilterPolicies() {
  return useQuery({
    queryKey: keys.policies,
    queryFn: () => apiClient.get<FilterPolicy[]>(ENDPOINTS.FILTER_POLICIES),
  });
}

export function useRoleFilterPolicies() {
  return useQuery({
    queryKey: keys.rolePolicies,
    queryFn: () => apiClient.get<RoleFilterPolicy[]>(ENDPOINTS.ROLE_FILTER_POLICIES),
  });
}

/** Kapsam listesinde nesne seçmek için; ad sırasıyla. */
export function usePolicyObjects() {
  return useQuery({
    queryKey: keys.objects,
    queryFn: async () => {
      const list = await apiClient.get<SystemObject[]>(ENDPOINTS.OBJECTS_M);
      return [...list].sort((a, b) =>
        (a.objectAlias || a.objectName || '').localeCompare(b.objectAlias || b.objectName || '', 'tr'),
      );
    },
    staleTime: 5 * 60_000,
  });
}

/** Kaydetme: kimliği boş olan yeni kuraldır (masaüstündeki ayrımın aynısı). */
export function useSavePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (policy: FilterPolicy) => {
      const isNew = !policy.filterPolicyId || policy.filterPolicyId.startsWith('00000000');
      const url = isNew ? ENDPOINTS.FILTER_POLICIES : ENDPOINTS.FILTER_POLICY(policy.filterPolicyId);
      return apiClient.post<{ success: boolean; message?: string }>(url, policy);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.policies }),
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filterPolicyId: string) =>
      apiClient.delete<{ success: boolean; message?: string }>(ENDPOINTS.FILTER_POLICY(filterPolicyId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.policies });
      void qc.invalidateQueries({ queryKey: keys.rolePolicies });
    },
  });
}

export function useAssignPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { roleId: string; filterPolicyId: string }) =>
      apiClient.post<{ success: boolean; message?: string }>(ENDPOINTS.ROLE_FILTER_POLICIES, {
        roleFilterPolicyId: '00000000-0000-0000-0000-000000000000',
        isActive: true,
        ...payload,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.rolePolicies }),
  });
}

export function useUnassignPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleFilterPolicyId: string) =>
      apiClient.delete<{ success: boolean; message?: string }>(ENDPOINTS.ROLE_FILTER_POLICY(roleFilterPolicyId)),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.rolePolicies }),
  });
}
